import type { ScenarioName, ScenarioSource } from './types';

/**
 * Mobile's exact scenario wire tokens. This union is the typo-checked source of truth: a
 * mistyped token in the map below (e.g. `SCENARIO_ENGAGE_APP_OPEN_HOME_FED`) fails to compile
 * rather than silently shipping and breaking the cross-platform join. Add a member when
 * reconciling a new scenario/source.
 */
type ScenarioWireValue = 'SCENARIO_ENGAGE_APP_OPEN_HOME_FEED';

/**
 * Maps a web `(name, source)` pair to mobile's exact scenario wire value, so the emitted
 * `scenario` property is byte-identical across web and mobile and cross-platform SLA
 * dashboards join on it directly. Web keeps `name` and `source` orthogonal; mobile bakes the
 * source into a single enum case per `(destination, entry point)` — this map reconciles the
 * two on emit.
 *
 * **Only add a row when adopting a mobile token is genuinely free (the scenario unifies
 * across iOS and Android).** For scenarios where mobile's naming diverges across platforms
 * (e.g. `reply_shown`: iOS `EngageConversationComposerReplyShown` vs Android
 * `EngageFeedComposerReplyShown`), do NOT shoehorn web's name to one platform — leave it
 * unmapped so web emits its stable web-native name, and reconcile the platforms in the
 * comparison KQL instead. The publisher interaction scenarios (`post_shown` / `reply_shown`)
 * are intentionally unmapped for this reason.
 */
const scenarioWireValue: Partial<Record<ScenarioName, Partial<Record<ScenarioSource, ScenarioWireValue>>>> = {
  open_home_feed: {
    engage_left_nav: 'SCENARIO_ENGAGE_APP_OPEN_HOME_FEED',
  },
};

/**
 * Returns mobile's exact wire value for a `(name, source)` pair, or the readable `name` when
 * the pair has no mapping yet (an unreconciled source, or a scenario emitted without a
 * source). Falling back to `name` keeps the scenario reportable and web can still slice by
 * the orthogonal `source` property.
 */
export const composeScenarioWireValue = (
  name: ScenarioName,
  source?: ScenarioSource
): ScenarioName | ScenarioWireValue => {
  if (!source) {
    return name;
  }

  return scenarioWireValue[name]?.[source] ?? name;
};
